/**
 * PlanChange — in-app plan change (upgrade / downgrade) component.
 *
 * in-app-checkout Phase 3 Session 3.5.
 *
 * Handles the Principal ↔ Sovereign plan-change flow (the two PAID tiers only).
 * Bespoke is NOT an in-place plan-change target and must be excluded before
 * reaching this component (its CTA routes to the awaiting-response flow).
 *
 * Design (compositional-A/B fidelity per refs/prototype-fidelity-spec.md § Screen C):
 *   1. Heading — Membership eyebrow + serif "Upgrade to {tier}" / "Switch to {tier}"
 *      + back affordance.
 *   2. Target plan radio cards — the two paid tiers; selected card = TARGET,
 *      the other = "Current plan". Selecting the other card flips the direction.
 *   3. Order details panel — CycleTransition (Today → Now on) + proration rows.
 *      Reads the REAL proration preview from `useProrationPreview`. While loading,
 *      shows skeleton rows. On error, shows an inline error + retry.
 *   4. Timing notice — BOTH directions are EFFECTIVE IMMEDIATELY (`always_invoice`
 *      mirrors Customer Portal config). Tone: neutral/info for both (downgrade
 *      is a clean immediate action, NOT a deferred "scheduled" state).
 *   5. Terms checkbox (native, accent-tinted) + Confirm CTA.
 *   6. Success state — inline after confirm.
 *
 * Copy rules:
 *   - Dollar amounts ARE shown in the order details (checkout/order context,
 *     not a usage meter — the copy-rule exemption applies).
 *   - NO "Anthropic", NO COGS math, NO usage_multiplier.
 *
 * Token discipline:
 *   - No raw hex in this file.
 *   - One whitelisted `rgba()` in the prototype's recommended-card glow is NOT
 *     reproduced here (plan-change cards don't carry the recommended glow).
 *
 * @param currentTierId   — user's current tier id (e.g. `'principal'`)
 * @param currentTierName — display name of the current tier
 * @param targetTierId    — pre-selected target tier id (from caller; user can
 *                          flip via radio cards)
 * @param currentSortOrder — sort_order of the current tier (for upgrade vs downgrade determination)
 * @param periodEnd        — ISO timestamp for the renewal date label
 * @param onSuccess        — called after the plan change mutation succeeds
 * @param onBack           — called when user clicks the ← back affordance
 * @param userId           — user id (for mutation cache invalidation)
 */

import { useState } from 'react';
import clsx from 'clsx';
import {
  ArrowLeft,
  ArrowRight,
  CalendarClock,
  Check,
  ShieldCheck,
} from 'lucide-react';
import { Panel } from '@/components/ui/Panel';
import { Button } from '@/components/ui/Button';
import { Skeleton } from '@/components/ui/Skeleton';
import { usePlanChange } from '@/hooks/use-plan-change';
import { useProrationPreview } from '@/hooks/use-proration-preview';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** US dollar formatting — `$499` (no cents on whole-dollar amounts, 2 dp otherwise). */
function formatAmount(cents: number): string {
  const usd = cents / 100;
  return Number.isInteger(usd) ? `$${usd}` : `$${usd.toFixed(2)}`;
}

/** Short human date — `Jun 5, 2026`. */
function formatDateStr(iso: string | number): string {
  const d = typeof iso === 'number' ? new Date(iso) : new Date(iso);
  return d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

interface MembershipEyebrowProps {
  className?: string;
}

function MembershipEyebrow({ className }: MembershipEyebrowProps) {
  return (
    <div
      className={clsx(
        'font-mono text-[11px] font-medium uppercase tracking-[0.22em] text-accent',
        className
      )}
    >
      Membership
    </div>
  );
}

interface PlanRadioCardProps {
  selected: boolean;
  isCurrent: boolean;
  onClick: () => void;
  name: string;
  tagline: string;
  priceLine: string;
  tag?: string;
  testid: string;
}

function PlanRadioCard({
  selected,
  isCurrent,
  onClick,
  name,
  tagline,
  priceLine,
  tag,
  testid,
}: PlanRadioCardProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      data-selected={selected}
      data-current={isCurrent}
      aria-pressed={selected}
      className={clsx(
        'relative flex w-full flex-col items-start gap-2 rounded-[3px] border px-4 py-4 text-left transition-colors duration-200',
        selected
          ? 'border-accent/50 bg-accent-faint'
          : 'border-panel-border bg-panel-bg hover:border-panel-border-hi'
      )}
    >
      <span className="flex w-full items-center justify-between">
        <span
          className={clsx(
            'flex h-4 w-4 shrink-0 items-center justify-center rounded-full border',
            selected ? 'border-accent' : 'border-text-dim'
          )}
        >
          {selected && <span className="h-2 w-2 rounded-full bg-accent" />}
        </span>
        <span className="flex items-center gap-1.5">
          {isCurrent && (
            <span className="rounded-[2px] border border-panel-border-hi bg-input-bg px-1.5 py-0.5 font-mono text-[8px] font-semibold uppercase tracking-[0.4px] text-text-muted">
              Current plan
            </span>
          )}
          {tag && (
            <span className="rounded-[2px] border border-accent/20 bg-accent-dim px-1.5 py-0.5 font-mono text-[8px] font-semibold uppercase tracking-[0.4px] text-accent">
              {tag}
            </span>
          )}
        </span>
      </span>
      <span className="font-serif text-[18px] font-semibold leading-tight tracking-[-0.01em] text-text-bright">
        {name}
      </span>
      <span className="font-body text-[12px] leading-snug text-text-muted">
        {tagline}
      </span>
      <span className="font-mono text-[11px] tabular-nums text-text-muted">
        {priceLine}
      </span>
    </button>
  );
}

interface OrderRowProps {
  label: string;
  sublabel?: string;
  value: string;
  emphasis?: 'total';
  divider?: boolean;
}

function OrderRow({
  label,
  sublabel,
  value,
  emphasis,
  divider,
}: OrderRowProps) {
  return (
    <div
      className={clsx(
        'flex items-start justify-between gap-4 py-2.5',
        divider && 'border-t border-panel-border'
      )}
    >
      <span className="flex flex-col gap-0.5">
        <span
          className={clsx(
            'font-mono uppercase tracking-[0.5px]',
            emphasis === 'total'
              ? 'text-[10px] font-semibold text-text-bright'
              : 'text-[9px] text-text-muted'
          )}
        >
          {label}
        </span>
        {sublabel && (
          <span className="font-mono text-[9px] tracking-[0.4px] text-text-dim">
            {sublabel}
          </span>
        )}
      </span>
      <span
        className={clsx(
          'font-mono tabular-nums',
          emphasis === 'total'
            ? 'text-base font-bold text-accent'
            : 'text-sm text-text-bright'
        )}
      >
        {value}
      </span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tier data shape (locally-typed — Phase 1/2 contracts are SKELETON)
// ---------------------------------------------------------------------------

/** Minimal tier info needed for plan-change (passed from the caller). */
export interface PlanChangeTierInfo {
  id: string;
  displayName: string;
  tagline: string;
  /** Monthly price in USD dollars (not cents). null = contact-only tier. */
  monthlyPriceUsd: number | null;
  sortOrder: number;
}

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface PlanChangeProps {
  /** The user's current subscription tier id. */
  currentTierId: string;
  /** The two paid tiers available for in-place plan change (no Bespoke). */
  paidTiers: [PlanChangeTierInfo, PlanChangeTierInfo];
  /**
   * Pre-selected target tier id (from the caller — e.g. the pricing grid
   * CTA that launched this view). The user can flip via the radio cards.
   */
  initialTargetTierId: string;
  /** ISO timestamp for the renewal date label. */
  periodEnd: string;
  /** User id — passed to `usePlanChange` for cache invalidation. */
  userId: string | undefined;
  /** Called after the plan change mutation succeeds. */
  onSuccess: () => void;
  /** Called when user clicks ← back. */
  onBack?: () => void;
}

// ---------------------------------------------------------------------------
// Public component
// ---------------------------------------------------------------------------

/**
 * PlanChange — renders the full plan-change flow inline.
 *
 * Shows radio cards for the two paid tiers, a live proration-preview order
 * panel, a timing notice, a terms checkbox, and a Confirm CTA. On success
 * transitions to an inline success panel.
 */
export function PlanChange({
  currentTierId,
  paidTiers,
  initialTargetTierId,
  periodEnd,
  userId,
  onSuccess,
  onBack,
}: PlanChangeProps) {
  const [selectedId, setSelectedId] = useState(initialTargetTierId);
  const [agreed, setAgreed] = useState(false);
  const [succeeded, setSucceeded] = useState(false);
  const [confirmError, setConfirmError] = useState<string | null>(null);

  const { mutateAsync: changePlan, isPending } = usePlanChange(userId);

  const {
    preview,
    isLoading: previewLoading,
    isError: previewError,
    refetch: refetchPreview,
  } = useProrationPreview(
    selectedId !== currentTierId ? selectedId : undefined
  );

  // Derive direction from sort orders.
  const targetTier = paidTiers.find((t) => t.id === selectedId) ?? paidTiers[1];
  const fromTier = paidTiers.find((t) => t.id !== selectedId) ?? paidTiers[0];
  const isUpgrade = targetTier.sortOrder > fromTier.sortOrder;
  const renewsOn = formatDateStr(periodEnd);

  async function handleConfirm() {
    if (!agreed || isPending) return;
    setConfirmError(null);
    try {
      await changePlan({ tier_id: selectedId });
      setSucceeded(true);
      onSuccess();
    } catch {
      setConfirmError(
        'Could not complete the plan change. Please try again in a moment.'
      );
    }
  }

  // --- Success panel ---
  if (succeeded) {
    return (
      <div data-testid="plan-change-success">
        <Panel title="Plan change confirmed" badge="DONE">
          <div className="flex flex-col items-center gap-4 py-6 text-center">
            <span className="flex h-12 w-12 items-center justify-center rounded-full border border-accent/30 bg-accent-dim text-accent">
              <ShieldCheck size={24} />
            </span>
            <div className="flex flex-col items-center gap-1.5">
              <MembershipEyebrow />
              <span
                className="font-serif text-[22px] font-semibold tracking-[-0.01em] text-text-bright"
                data-testid="plan-change-success-title"
              >
                You&rsquo;re now on {targetTier.displayName}
              </span>
              <span className="max-w-[380px] font-body text-xs leading-relaxed text-text-muted">
                {isUpgrade
                  ? `Your upgraded capacity is available immediately. Renews at $${targetTier.monthlyPriceUsd}/mo on ${renewsOn}.`
                  : `${targetTier.displayName} applies now. A prorated credit for the unused portion of your previous cycle was applied toward future invoices. Renews at $${targetTier.monthlyPriceUsd}/mo on ${renewsOn}.`}
              </span>
            </div>
          </div>
        </Panel>
      </div>
    );
  }

  // --- Form ---
  return (
    <div data-testid="plan-change-form">
      {/* Heading */}
      <div className="mb-6 flex items-start gap-3">
        {onBack && (
          <button
            type="button"
            onClick={onBack}
            aria-label="Back"
            data-testid="plan-change-back"
            className="mt-1.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-[2px] border border-panel-border bg-panel-bg text-text-muted transition-colors hover:border-panel-border-hi hover:text-text-bright"
          >
            <ArrowLeft size={14} />
          </button>
        )}
        <div className="flex flex-col gap-1">
          <MembershipEyebrow />
          <h2
            className="font-serif text-[26px] font-semibold leading-[1.1] tracking-[-0.01em] text-text-bright"
            data-testid="plan-change-heading"
          >
            {isUpgrade
              ? `Upgrade to ${targetTier.displayName}`
              : `Switch to ${targetTier.displayName}`}
          </h2>
        </div>
      </div>

      <div className="flex flex-col gap-4">
        {/* Target plan radio cards */}
        <div
          className="grid grid-cols-2 gap-2"
          data-testid="plan-change-radio-cards"
        >
          {paidTiers.map((t) => (
            <PlanRadioCard
              key={t.id}
              selected={selectedId === t.id}
              isCurrent={t.id === currentTierId}
              onClick={() => setSelectedId(t.id)}
              name={t.displayName}
              tagline={t.tagline}
              priceLine={
                t.monthlyPriceUsd !== null
                  ? `$${t.monthlyPriceUsd}/month`
                  : 'Custom pricing'
              }
              tag={isUpgrade && t.id === selectedId ? 'Immediate' : undefined}
              testid={`plan-change-card-${t.id}`}
            />
          ))}
        </div>

        {/* Order details panel — proration preview */}
        <Panel title="Order details">
          <div className="flex flex-col">
            {/* Cycle transition — Today → Now on */}
            <div
              className="grid grid-cols-[1fr_auto_1fr] items-start gap-3 pb-3"
              data-testid="plan-change-cycle-transition"
            >
              {/* Left — current tier (what the user is leaving) */}
              <div className="flex flex-col gap-0.5">
                <span className="font-mono text-[8px] font-semibold uppercase tracking-[1.4px] text-text-muted">
                  Today
                </span>
                <span className="font-serif text-[16px] font-semibold leading-tight text-text-bright">
                  {fromTier.displayName}
                </span>
                <span className="font-body text-[11px] leading-snug text-text-muted">
                  {fromTier.tagline}
                </span>
              </div>

              {/* Arrow */}
              <span className="mt-5 text-accent">
                <ArrowRight size={16} />
              </span>

              {/* Right — target tier (what takes effect now) */}
              <div className="flex flex-col gap-0.5">
                <span className="font-mono text-[8px] font-semibold uppercase tracking-[1.4px] text-accent">
                  Now on
                </span>
                <span className="font-serif text-[16px] font-semibold leading-tight text-text-bright">
                  {targetTier.displayName}
                </span>
                <span className="font-body text-[11px] leading-snug text-text-muted">
                  {targetTier.monthlyPriceUsd !== null
                    ? `$${targetTier.monthlyPriceUsd}/mo`
                    : 'Custom pricing'}
                </span>
                <span className="font-mono text-[9px] tracking-[0.4px] text-text-dim">
                  Renews {renewsOn}
                </span>
              </div>
            </div>

            {/* Proration rows */}
            <div className="border-t border-panel-border">
              {previewLoading && (
                <div className="flex flex-col gap-1.5 py-3">
                  <Skeleton className="h-5" />
                  <Skeleton className="h-5" />
                </div>
              )}

              {previewError && (
                <div className="flex items-center justify-between gap-3 py-3">
                  <span className="font-mono text-xs text-warn">
                    Could not load proration preview.
                  </span>
                  <button
                    type="button"
                    onClick={() => void refetchPreview()}
                    data-testid="plan-change-preview-retry"
                    className="font-mono text-[10px] uppercase tracking-[0.6px] text-accent hover:underline"
                  >
                    Retry
                  </button>
                </div>
              )}

              {!previewLoading && !previewError && preview && (
                <>
                  {isUpgrade ? (
                    <OrderRow
                      label="Prorated charge today"
                      sublabel="For the remainder of this billing cycle"
                      value={formatAmount(preview.amount_due_cents)}
                    />
                  ) : (
                    <OrderRow
                      label="Prorated credit applied"
                      sublabel="Unused portion of your current cycle, toward future invoices"
                      value={`+${formatAmount(preview.amount_due_cents)}`}
                    />
                  )}
                  <OrderRow
                    label="Total due today"
                    sublabel={
                      isUpgrade
                        ? undefined
                        : 'A credit is issued — nothing is charged now'
                    }
                    value={
                      isUpgrade ? formatAmount(preview.amount_due_cents) : '$0'
                    }
                    emphasis="total"
                    divider
                  />
                </>
              )}

              {/* Skeleton state while preview is loading */}
              {!previewLoading && !previewError && !preview && (
                <OrderRow
                  label="Total due today"
                  value="—"
                  emphasis="total"
                  divider
                />
              )}
            </div>
          </div>
        </Panel>

        {/* Timing notice — BOTH directions are EFFECTIVE IMMEDIATELY */}
        <div
          role="note"
          data-testid="plan-change-notice"
          className="flex items-start gap-2.5 rounded-[3px] border border-panel-border bg-input-bg px-3.5 py-2.5"
        >
          <CalendarClock
            size={13}
            className="mt-0.5 shrink-0 text-text-muted"
            aria-hidden
          />
          <span className="font-body text-xs leading-relaxed text-text-muted">
            {isUpgrade ? (
              <>
                Effective immediately. You&rsquo;ll be charged a prorated amount
                for the rest of this cycle today. Your plan renews at $
                {targetTier.monthlyPriceUsd}/mo on {renewsOn}.
              </>
            ) : (
              <>
                Effective immediately. You&rsquo;ll move to{' '}
                {targetTier.displayName} now and we&rsquo;ll credit the unused
                portion of your current cycle toward future invoices. Renews at
                ${targetTier.monthlyPriceUsd}/mo on {renewsOn}.
              </>
            )}
          </span>
        </div>

        {/* Inline confirm error */}
        {confirmError && (
          <div
            role="alert"
            data-testid="plan-change-error"
            className="rounded-[3px] border border-danger/15 bg-danger-dim px-4 py-2.5"
          >
            <p className="font-mono text-xs text-danger">{confirmError}</p>
          </div>
        )}

        {/* Terms checkbox (native, accent-tinted — convention per CLAUDE.md) */}
        <label className="flex cursor-pointer items-start gap-2.5">
          <input
            type="checkbox"
            checked={agreed}
            onChange={(e) => setAgreed(e.target.checked)}
            data-testid="plan-change-terms"
            className="mt-0.5 accent-accent"
          />
          <span className="font-body text-xs leading-relaxed text-text-muted">
            You agree that Arcanum will charge your card per the order details
            above, in accordance with our terms. You can cancel at any time in
            your account settings.
          </span>
        </label>

        {/* Confirm CTA — Membership aesthetic */}
        <button
          type="button"
          onClick={() => void handleConfirm()}
          disabled={!agreed || isPending || previewLoading}
          data-testid="plan-change-confirm"
          className={clsx(
            'w-full py-3',
            'bg-accent border border-accent rounded-[3px]',
            'font-mono text-[0.78rem] font-medium uppercase tracking-[0.14em]',
            'text-void cursor-pointer hover:brightness-110 transition-[filter] duration-200',
            'disabled:opacity-60 disabled:cursor-not-allowed'
          )}
        >
          {isPending ? (
            'Processing…'
          ) : (
            <span className="flex items-center justify-center gap-1.5">
              <Check size={13} />
              {isUpgrade
                ? 'Confirm changes'
                : `Confirm ${targetTier.displayName}`}
            </span>
          )}
        </button>
      </div>
    </div>
  );
}
